import React, { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import './styles/GallerySelector.scss'
import Icon from './uiComponents/Icon'
import { GalleryCropper } from './uiComponents/GallerySelector/GalleryCropper'
import { ImageSelectorModal } from './uiComponents/GallerySelector/ImageSelectorModal'
import Upload from './uiComponents/GallerySelector/Upload'
import {
  assetAllowedToBeCropped,
  isPDF,
  isTiff,
  replaceMessageKeys,
  uploadAsset
} from './uiComponents/GallerySelector/uploadUtils'
import PreviewModal from './uiComponents/GallerySelector/PreviewModal'
import LoadingDots from './uiComponents/LoadingDots/LoadingDots'
import ImageLoader from './uiComponents/GallerySelector/ImageLoader'
import { validateItem } from './uiComponents/GallerySelector/uploadUtils'

const EDITOR_SETTINGS = {
  manually: 0,
  isInvalid: 1,
  anyImage: 2,
}

const formatType = (options, format, defType, stringCase = 'toLowerCase') => {
  return options ? options.map((type) => `${format}${type[stringCase]()}`) : defType
}

const extractPropertyId = (value) => {
  const re = /([a-f0-9]{8}(?:-[a-f0-9]{4}){3}-[a-f0-9]{12})$/i
  const match = re.exec(value)
  return match[1]
}

const getImageContainerClass = (item, isUpload, isGallery) => {
  let image_container_class = 'image-container'
  if (!item?.ThumbnailUrl) {
    if (isUpload) {
      image_container_class += ' drag-to-upload'
    } else if (isGallery) {
      image_container_class += ' click-to-select'
    }
  }
  return image_container_class
}

const getPDFInfoMessages = (validations, t) => {
  const messages = []
  validations
    .filter(validation => validation.validationType !== 'ImageMaxSize')
    .forEach(validation => {
      if (['ImageWidthRange', 'ImageHeightRange', 'ImageRatio'].includes(validation.validationType)) {
        const { minimumValue, maximumValue } = validation.parameters
        let translationKey = `GallerySelectorDuc.Info.${validation.validationType}`

        if (maximumValue === 2147483647) {
          translationKey += '.MinValue'
          messages.push(t(translationKey, { minimumValue, maximumValue }))
        } else if (minimumValue < 0) {
          translationKey += '.MaxValue'
          messages.push(t(translationKey, { minimumValue, maximumValue }))
        } else {
          messages.push(t(translationKey + '.MinValue', { minimumValue, maximumValue }))
          messages.push(t(translationKey + '.MaxValue', { minimumValue, maximumValue }))
        }
      }
    })
  return messages
}

const GallerySelector = (props) => {
  const {
    value,
    readonly,
    onChange,
    t,
    uiSchema,
    id,
    schema: { custom: { validation } },
    formContext: { UStoreProvider }
  } = props
  const [isDragging, setIsDragging] = useState(false)
  const [selectionType, setSelectionType] = useState({ Gallery: false, MyImages: false })
  const [modalOpened, setModalOpened] = useState({ GallerySelector: false, Cropper: false, PreviewModal: false })
  const [selectedItem, setSelectedItem] = useState(null)
  const [isUploading, setIsUploading] = useState(false)
  const [warnings, setWarnings] = useState([])
  const [infos, setInfos] = useState([])
  const [clearUpload, setClearUpload] = useState(0)
  const [currentFileName, setCurrentFileName] = useState(null)

  const uiSchemaCustomOption = uiSchema['ui:options'].custom
  const allowedExtensions = uiSchemaCustomOption.allowedExtensions
  const isGallery = uiSchemaCustomOption.hideGallery !== true
  const isUpload = uiSchemaCustomOption.allowUpload
  const isCircleCrop = uiSchemaCustomOption.circleCropper
  const libraryAssetsAllowEditing = uiSchemaCustomOption.libraryAssetsAllowEditing
  const { openImageEditorValue, allowEditingUploadedImages } = uiSchemaCustomOption
  const propertyId = extractPropertyId(id)
  const currentProduct = UStoreProvider.state.get()?.customState?.currentProduct
  const previewButtonVisible = !currentProduct?.Configuration?.Proof?.FastPreviewEnabled
  const formattedAllowedExtensions = formatType(allowedExtensions, '*.', null, 'toUpperCase')
  const { isInvalid, anyImage} = EDITOR_SETTINGS
  const autoOpenEditor = (displayEditor = [], fileName) => {
    if (!displayEditor.length) return null

    if (allowEditingUploadedImages &&
      displayEditor.includes(openImageEditorValue) &&
      assetAllowedToBeCropped(fileName)
    ) {
      setModalOpened((modalOpenedState) => ({ ...modalOpenedState, Cropper: true }))
    }
  }

  const passErrorAppleImageSize = () => {
    const maxSize = validation.find(v => v.validationType === 'ImageMaxSize')
    if (maxSize) {
      onChange(value, replaceMessageKeys([maxSize.messageKey], uiSchema))
      return null
    }
  }

  const onUploadHandle = async ({ file, imageData, isCropperOpen }) => {
    setInfos([])
    setWarnings([])
    setIsUploading(true)
    setIsDragging(false)

    uploadAsset(propertyId, file, imageData, validation, file.name === currentFileName)
      .then(({
      errors,
      item,
      canBeCropped
    }) => {
      setCurrentFileName(file.name)
      setIsUploading(false)
      if (isPDF(file.name)) {
        setInfos(getPDFInfoMessages(validation, t))
      }
        const handleItemSelected = ({ item, selectionType }) => {
          setSelectionType(selectionType)
          setSelectedItem(item)
          onChange(item.AssetID, replaceMessageKeys(errorList, uiSchema))
        }
        const errorList = errors.length ? errors : []
        const maxSize = validation.find(v => v.validationType === 'ImageMaxSize')
        if (maxSize) {
          if (errorList.find(e => e === maxSize.messageKey)) {
            handleItemSelected({ item: value, selectionType: { Gallery: false, MyImages: false } })
            return
          }
        }
        handleItemSelected({ item, selectionType: { Gallery: isPDF(item.FileName), MyImages: canBeCropped } })
        const displayEditor = errorList.length > 0 ? [anyImage, isInvalid] : [anyImage];
        if (!isCropperOpen) {
          autoOpenEditor(displayEditor, item.FileName);
        }
      })
  }

  const onDragEnter = () => {
    if (isUpload) {
      setIsDragging(true)
    }
  }
  const onDragLeave = () => setIsDragging(false)
  const onDropError = (error) => {
    onChange('__xmpie__error__', [error])
    setIsDragging(false)
  }
  const onDrop = () => {}
  const onCropperClose = () => setModalOpened({ ...modalOpened, Cropper: false })
  const onCropperChange = (imageData, isCropperOpen) => {
    if (imageData) {
      const arr = imageData.split(',')
      const mime = arr[0].match(/:(.*?);/)[1]
      const bstr = atob(arr[1])
      let n = bstr.length
      const u8arr = new Uint8Array(n)
      while (n--) u8arr[n] = bstr.charCodeAt(n)

      onUploadHandle({ file: new File([u8arr], selectedItem.FileName, { type: mime }), imageData, isCropperOpen })
      return
    }
    const errors = validateItem(selectedItem, validation)
    if (errors.length > 0) {
      onChange('__xmpie__error__', replaceMessageKeys(errors, uiSchema))
      return
    }
    onChange(selectedItem.AssetID, [])
  }

  useEffect(() => {
    (async () => {
      if (value && value !== '__xmpie__error__' && value !== '__xmpie__clear__') {
        const item = await window.UStoreProvider.state.assets.getAsset(value)
        setSelectedItem(item)
        setSelectionType({
          Gallery: (!libraryAssetsAllowEditing && !isTiff(item.FileName)) || isPDF(item.FileName),
          MyImages: libraryAssetsAllowEditing && assetAllowedToBeCropped(item.FileName),
        })
        setCurrentFileName(item.FileName)
      }
    })()
  }, [])

  const onEditCropper = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setModalOpened({ ...modalOpened, Cropper: true })
  }

  const onPreviewModalClick = (e) => {
    setModalOpened({ ...modalOpened, PreviewModal: true })
    e.stopPropagation()
  }

  const onPreviewModalClose = () => setModalOpened({ ...modalOpened, PreviewModal: false })

  const onGalleryClick = (e) => {
    setModalOpened({ ...modalOpened, GallerySelector: true })
    e.stopPropagation()
  }
  const onImageSelectorCancel = () => setModalOpened({ ...modalOpened, GallerySelector: false })
  const onImageSelectorSelect = async (item) => {
    const newSelectedItem = await window.UStoreProvider.state.assets.getAsset(item.AssetID)
    setInfos([])
    setWarnings([])
    setSelectedItem(newSelectedItem)
    setModalOpened({ ...modalOpened, GallerySelector: false })
    setSelectionType({
      Gallery: isPDF(newSelectedItem.FileName) || (!libraryAssetsAllowEditing && assetAllowedToBeCropped(newSelectedItem.FileName) && !newSelectedItem.IsUploaded),
      MyImages: assetAllowedToBeCropped(newSelectedItem.FileName) && (newSelectedItem.IsUploaded || (!newSelectedItem.IsUploaded && libraryAssetsAllowEditing)),
    })
    const errors = replaceMessageKeys(validateItem(newSelectedItem, validation), uiSchema)

    if (isPDF(newSelectedItem.FileName)) {
      setInfos(getPDFInfoMessages(validation, t))
      onChange(newSelectedItem.AssetID)
      return
    }
    const errorList = errors.length ? errors : []
    setWarnings(errorList)
    onChange(newSelectedItem.AssetID, errorList)
    const displayEditor = errorList.length > 0 ? [anyImage, isInvalid] : [anyImage];
    if (libraryAssetsAllowEditing || newSelectedItem.IsUploaded) {
      autoOpenEditor(displayEditor, newSelectedItem.FileName);
    }
  }

  const onClearSelection = (e) => {
    e && e.stopPropagation()
    setClearUpload(clearUpload + 1)
    setSelectedItem(null)
    setSelectionType({ Gallery: false, MyImages: false })
    setCurrentFileName(null)
    setInfos([])
    onChange('__xmpie__clear__')
  }

  const onDucClick = (e) => {
    if (!selectedItem && isUpload) {
      return
    }

    if (!selectedItem && !isUpload && isGallery) {
      onGalleryClick(e)
      return
    }

    if (isGallery && !selectedItem?.IsUploaded) {
      if (assetAllowedToBeCropped(selectedItem.FileName) && libraryAssetsAllowEditing) {
        onEditCropper(e)
        return
      }
      onPreviewModalClick(e)
      return
    }

    if (selectedItem?.IsUploaded && assetAllowedToBeCropped(selectedItem.FileName)) {
      onEditCropper(e)
    }
  }

  const uploadClickEnabled = (isUpload && !selectedItem) || selectedItem?.IsUploaded

  return <div className={isDragging ? 'xmp-gs-wrapper-dragging' : 'xmp-gs'}>
    <Upload className="xmp-gs-wrapper" t={t} onDrop={onDrop} clearUpload={clearUpload}
            enabled={isUpload} clickEnabled={uploadClickEnabled} onDropError={onDropError} onUpload={onUploadHandle}
            onDragEnter={onDragEnter} allowedExtensions={allowedExtensions} validation={validation} passErrorAppleImageSize={passErrorAppleImageSize}
            tabIndex="-1"
    >
      <div className={`xmp-gs-duc-content ${isUploading ? 'hide-bg' : ''}`} onClick={onDucClick}>
        <div className={getImageContainerClass(selectedItem, isUpload, isGallery)}>
          {selectedItem && !readonly &&
            <Icon name="closeBig.svg" size="16px" className="close-icon" onClick={onClearSelection}/>}
          {selectionType.MyImages &&
            <Icon name="pencil.svg" size="20px" classes="icon-container pencil" onClick={onEditCropper}/>}
          {selectionType.Gallery &&
            <Icon name="zoom.svg" size="20px" classes="icon-container" onClick={onPreviewModalClick}/>}
          {!isUploading && selectedItem?.ThumbnailUrl &&
            <ImageLoader
              src={selectedItem.ThumbnailUrl}
              onImageLoad={() => {}}
              loadingDots={true}
              scrollableRef={{ current: document.body }}/>
          }
          {isUploading && <LoadingDots/>}
        </div>
        <div className="controls">
          {isUpload &&
            <Upload noDrop enabled={true} clickEnabled={true} onDropError={onDropError} onUpload={onUploadHandle} t={t}
                    allowedExtensions={allowedExtensions} className="upload-file" clearUpload={clearUpload} validation={validation}
                    passErrorAppleImageSize={passErrorAppleImageSize} tabIndex="0">
              <Icon name="upload.svg" size="20px" className="upload-icon"/>{t('GallerySelectorDuc.Upload')}
            </Upload>}

          {isGallery && <div className="gallery-button" onClick={onGalleryClick} tabIndex="0" data-xmpie-focusable="true"
            onKeyDown={(e) => e.key === 'Enter' && onGalleryClick(e)}
          >
            <Icon name="gallery.svg" size="20px" className="upload-icon"/>{isUpload ? t('GallerySelectorDuc.Gallery') :
            t('GallerySelectorDuc.GalleryLong')}
          </div>}
        </div>
      </div>
      <div className="xmp-gs-drop-zone"
           onDragLeave={onDragLeave}>{t('GallerySelectorDuc.DropImageHere')}</div>
    </Upload>
    <div className="xmp-gs-infos">
      <div className="xmp-gs-infos-content">
        {t('GallerySelectorDuc.AllowedFileTypes', { types: formattedAllowedExtensions.join(', ') })}
      </div>
      {infos.map(info => (<div className="xmp-gs-infos-content">{info}</div>))}
    </div>
    {warnings.length > 0 && warnings.map((wrn) => (
      <div className="xmp-gs-warnings">
        <Icon name="warn.svg" width="14px" height="12px" className="xmp-gs-warnings-icon" />
        <div className="xmp-gs-warnings-content">
          {wrn}
        </div>
      </div>
    ))}
    {
      modalOpened.Cropper &&
      createPortal(<div className="xmp-gs-cropper">
        <GalleryCropper item={selectedItem} onClose={onCropperClose} onChange={onCropperChange}
                        validation={validation} uiSchema={uiSchema} t={t} isCircleCrop={isCircleCrop}
                        showApply={previewButtonVisible}/>
      </div>, document.body)}
    {modalOpened.GallerySelector &&
      createPortal(<ImageSelectorModal onClose={onImageSelectorCancel} uiSchema={uiSchema} validation={validation}
                                       onSelect={onImageSelectorSelect} t={t} id={propertyId}
                                       selectedItem={selectedItem}
                                       onClearSelection={onClearSelection} value={value}
                                       setClearUpload={setClearUpload}
        />, document.body)}
    {modalOpened.PreviewModal &&
      createPortal(<PreviewModal item={selectedItem} t={t} onClose={onPreviewModalClose}/>, document.body)}
  </div>

}

if (typeof window !== 'undefined') {
  window.uStoreDucs = window.uStoreDucs || []
  window.uStoreDucs.push({ name: 'gallerySelector', component: GallerySelector, displayName: 'Gallery Image Selector' })
}

export default GallerySelector
